<template>
  <div>
    <overlay
      key="productHighlightDrawer"
      block-class="product-highlights-drawer"
      direction="left"
      namespace="productHighlightDrawer"
      show-overlay
      show-close
      type="drawer"
    >
      <template #body>
        <responsive-image
          v-if="currentFeature.imageType === 'image' && currentFeature"
          class="product-highlights-drawer__image"
          :image="currentFeature.image"
          ratio="3:2"
        />

        <responsive-video
          v-if="currentFeature.imageType === 'video' && currentFeature"
          class="product-highlights-drawer__video"
          :video-src="currentFeature.video"
          ratio="3:2"
          :lazyload="false"
          autoplay-enabled="true"
        />

        <div class="product-highlights-drawer__content">
          <h3
            v-if="currentFeature && currentFeature.title"
            class="text-heading-m text-heading-l-desktop"
            v-text="currentFeature.title"
          />
          <div
            v-if="currentFeature && currentFeature.subcopy"
            class="rte mt-l"
            v-html="currentFeature.subcopy"
          />
        </div>
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Product highlights (product-highlights)
 * -----------------------------------------------------------------------------
 * Interactive section to highlight product features.
 * - Limited interactivity section.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 *
 */
import ResponsiveImage from '~global/images/responsive-image'
import ResponsiveVideo from '~global/responsive-video/responsive-video'
import Overlay from '~global/overlay/overlay'
import { debounce } from '~/helpers/general'
import classes from '~/config/classes'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'ProductHighlightsDrawer',
  components: {
    ResponsiveImage,
    ResponsiveVideo,
    Overlay,
  },
  props: {
    blocks: {
      type: [Array],
      default: () => {
        return []
      },
    },
    sectionId: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      currentFeature: {},
    }
  },
  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

  },
  watch: {

    /**
     * Watch active overlay to reset current feature.
     */
    getActiveOverlay() {
      if (!this.getActiveOverlay) {
        this.currentFeature = {}
        this.deactivateAllTriggers()
      }
    },

  },
  mounted() {
    this.container = document.querySelector(`#vue-element .product-highlights-${this.sectionId}`)
    if (!this.container) {
      return
    }

    this.bindEventListeners()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Handle zoom click.
     */
    handleZoomClick(feature) {
      this.currentFeature = feature

      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'productHighlightDrawer',
      })
    },

    /**
     * Bind event listeners.
     */
    bindEventListeners() {
      this.overlayTriggers = this.container.querySelectorAll('[data-highlight-id]')

      if (!this.overlayTriggers) {
        return
      }

      this.overlayTriggers.forEach((highlight) => {
        highlight.addEventListener('click', (event) => {
          event.stopPropagation()
          this.deactivateAllTriggers()
          event.target.classList.add(classes.active)
          const highlightId = event.target.dataset.highlightId
          this.handleZoomClick(this.blocks[highlightId])
        })
      })

      this.bindMobileEventListeners()

      window.visualViewport.addEventListener('resize', debounce(() => {
        this.handleResize()
      }, 300), { passive: true })
    },

    /**
     * Deactivate all triggers.
     */
    deactivateAllTriggers() {
      if (this.overlayTriggers?.length > 0) {
        this.overlayTriggers.forEach((trigger) => {
          trigger.classList.remove(classes.active)
        })
      }
    },

    /**
     * Bind mobile event listeners.
     */
    bindMobileEventListeners() {
      const highlightTriggers = this.container.querySelectorAll('[data-mobile-highlight-id]')

      if (!highlightTriggers || highlightTriggers.length === 0) {
        return
      }

      highlightTriggers.forEach((highlight) => {
        highlight.addEventListener('click', (event) => {
          event.stopPropagation()
          const highlightId = event.target.dataset.mobileHighlightId
          this.handleZoomClick(this.blocks[highlightId])
        })
      })

      this.mobileTriggersInitialized = true
    },

    /**
     * Handle resize.
     */
    handleResize() {
      if (this.mobileTriggersInitialized) {
        return
      }

      this.bindMobileEventListeners()
    },
  },
}
</script>
